import React from 'react';
import * as Sentry from '@sentry/react';

export type SentryEnv = {
  VITE_SENTRY_DSN?: string;
  VITE_GIT_SHA?: string;
  MODE?: string;
};

function readEnv(): SentryEnv {
  // import.meta.env is statically replaced by Vite at build time, but we
  // wrap it so non-Vite test environments don't crash on access.
  try {
    return (import.meta as unknown as { env?: SentryEnv }).env ?? {};
  } catch {
    return {};
  }
}

let initialized = false;

export function initSentry(envOverride?: SentryEnv): boolean {
  if (initialized) return true;
  const env = envOverride ?? readEnv();
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: env.MODE || 'development',
    release: env.VITE_GIT_SHA,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    sendDefaultPii: false,
  });
  initialized = true;
  return true;
}

// Test-only: reset module state so each test can exercise initSentry afresh.
// Not exported via public surface in production callers.
export function __resetSentryForTests(): void {
  initialized = false;
}

const fallbackStyles = {
  wrap: {
    minHeight: '100vh',
    background: '#0A0A0F',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  } as React.CSSProperties,
  card: {
    maxWidth: '460px',
    width: '100%',
    border: '1px solid #1f1f2e',
    borderTop: '4px solid #7C3AED',
    borderRadius: '12px',
    padding: '32px',
    background: '#101019',
    boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
  } as React.CSSProperties,
  title: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 700,
    color: '#ffffff',
  } as React.CSSProperties,
  body: {
    marginTop: '12px',
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#cfcfe0',
  } as React.CSSProperties,
  row: {
    marginTop: '24px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  } as React.CSSProperties,
  reload: {
    background: '#7C3AED',
    color: '#ffffff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '8px',
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  feedback: {
    color: '#ffffff',
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  } as React.CSSProperties,
};

function handleReload(): void {
  try {
    window.location.reload();
  } catch {
    /* noop */
  }
}

function openReportDialog(eventId: string | undefined): void {
  try {
    Sentry.showReportDialog(eventId ? { eventId } : undefined);
  } catch {
    /* noop — Sentry not initialized in dev */
  }
}

type FallbackProps = {
  error: unknown;
  componentStack?: string | null;
  eventId?: string | null;
  resetError?: () => void;
};

export function SentryFallback(props: FallbackProps): React.ReactElement {
  const eventId = props.eventId ?? undefined;
  return (
    <div role="alert" style={fallbackStyles.wrap} data-testid="sentry-fallback">
      <div style={fallbackStyles.card}>
        <h1 style={fallbackStyles.title}>Something broke</h1>
        <p style={fallbackStyles.body}>
          We hit an unexpected error and the page can&rsquo;t continue. The CIVIX team has been
          notified. You can reload to keep working, or send us details about what you were doing.
        </p>
        <div style={fallbackStyles.row}>
          <button type="button" style={fallbackStyles.reload} onClick={handleReload}>
            Reload
          </button>
          <button
            type="button"
            style={fallbackStyles.feedback}
            onClick={() => openReportDialog(eventId)}
          >
            File feedback
          </button>
        </div>
      </div>
    </div>
  );
}

type BoundaryProps = {
  children: React.ReactNode;
};

export function SentryErrorBoundary({ children }: BoundaryProps): React.ReactElement {
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <SentryFallback
          error={errorData.error}
          componentStack={errorData.componentStack}
          eventId={errorData.eventId}
          resetError={errorData.resetError}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
